import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import queryString, { ParsedQuery } from 'query-string';

import { RequestQueryParams } from './types';

/**
 * Хук-обёртка над query-параметрами URL.
 * Позволяет читать, устанавливать, сбрасывать и пушить параметры в строку запроса.
 *
 * @returns Объект с текущими параметрами и утилитами для работы с ними
 */
export const useSearchParams = <T extends string | object>() => {
  const history = useHistory();
  const location = useLocation();

  const query = useMemo(
    () => queryString.parse(location.search) as ParsedQuery<T>,
    [location.search],
  );

  const hasParams = useCallback(
    (...params: string[]) => {
      const url = new URLSearchParams(location.search);

      return params.every(param => url.has(param));
    },
    [location.search],
  );

  const setParams = useCallback(
    (params: RequestQueryParams) => {
      const newQueryString = queryString.stringify(
        { ...query, ...params },
        { skipEmptyString: true },
      );

      history.replace({
        search: newQueryString ? `?${newQueryString}` : '',
      });
    },
    [history, query],
  );

  const resetParam = useCallback(
    (param: string) => {
      if (!query[param]) return;

      // eslint-disable-next-line @typescript-eslint/no-unused-vars,no-unused-vars
      const { [param]: omittedParam, ...otherParams } = query;

      history.replace({
        search: queryString.stringify(otherParams),
      });
    },
    [query, history],
  );

  const removeAllParams = useCallback(() => {
    history.replace({
      search: '',
    });
  }, [history]);

  const push = useCallback(
    (newQuery: RequestQueryParams, options?: ParsedQuery) => {
      history.push({
        search: queryString.stringify(
          {
            ...query,
            ...newQuery,
          },
          options,
        ),
      });
    },
    [history, query],
  );

  return {
    hasParams,
    setParams,
    resetParam,
    removeAllParams,
    query,
    push,
  };
};
